import { Vector2 } from "../../common/vector2.js";
import { Game } from "../../game.js";
import { ImgID } from "../../image_loader/img_id.js";
import { StatisticsStorage } from "../../storage/statistics_storage.js";
import type { GameStatistics } from "../../storage/statistics_storage.js";
import { Easing } from "../../transforms/transform_base.js";
import { TransformOpacity } from "../../transforms/transform_opacity.js";
import { TransformScale } from "../../transforms/transform_scale.js";
import { TransformValue } from "../../transforms/transform_value.js";
import { Vfx2DManager } from "../../webgl_2d/vfx_2d_manager.js";
import { Vfx2DRuntime } from "../../webgl_2d/vfx_2d_runtime.js";
import { UIElement } from "../common_ui_elements/ui_element.js";
import { UIElementHolder } from "../common_ui_elements/ui_element_holder.js";
import { UIImage } from "../common_ui_elements/ui_image.js";
import { AdventureRewardHud, AdventureRewardHudRefs } from "../screens/adventure_screen/adventure_reward_hud.js";
import { MenuScreen } from "../screens/menu_screen/menu_screen.js";

export type RewardFlyTarget = "coin" | "heart" | "avatar";

export interface RewardFlySource
{
  target: RewardFlyTarget;
  img_id: ImgID;
  center: Vector2;
  size: number;
  amount?: number;
  copies?: number;
}

export class RewardFlyToHud
{
  private static readonly HUD_APPEAR_MS = 120;
  private static readonly HUD_HOLD_MS = 650;
  private static readonly HUD_HIDE_MS = 160;
  private static readonly FLY_MS = 410;
  private static readonly FLY_STAGGER_MS = 60;
  private static readonly FLY_START_SCALE = 1.15;
  private static readonly FLY_END_SCALE = 0.45;
  private static readonly COUNT_UP_MS = 450;
  private static readonly IMPACT_MS = 280;
  private static readonly COINS_PER_SPRITE = 40;
  private static readonly MIN_COIN_SPRITES = 4;
  private static readonly MAX_COIN_SPRITES = 10;

  /** `coin_speed` > 1 shortens the coin flight and the gaps between coins (Vfx2DManager.addCoinsFly). */
  static play( ctx: CanvasRenderingContext2D, root: UIElement, sources: RewardFlySource[], stats_before: GameStatistics, coin_speed: number = 1 ): number
  {
    const now = performance.now();
    if ( !sources.length )
      return now;

    Game.instance.setVfxOverlayCanvasesVisible( true );

    // Over the menu the copy must match its top bar, "+" included.
    const hud = AdventureRewardHud.build( ctx, root, root.getRect().getW(), stats_before, { plus: MenuScreen.active !== null } );
    hud.row.setClickable( false );
    hud.row.setOpacityImmediate( 0 );
    hud.row.fadeIn( RewardFlyToHud.HUD_APPEAR_MS, now, Easing.EaseOutQuad );

    let coin_finish = 0;
    let heart_finish = 0;
    let avatar_finish = 0;
    let coin_size = 0;
    let heart_size = 0;
    let avatar_size = 0;

    for ( const source of sources )
    {
      if ( source.target === "coin" )
      {
        const count = Math.max(
          RewardFlyToHud.MIN_COIN_SPRITES,
          Math.min( RewardFlyToHud.MAX_COIN_SPRITES, Math.round( ( source.amount ?? 0 ) / RewardFlyToHud.COINS_PER_SPRITE ) )
        );
        coin_finish = Math.max( coin_finish, Vfx2DManager.addCoinsFly( source.center, hud.coin.target, count, now, coin_speed ) );
        coin_size = Math.max( coin_size, source.size );
        continue;
      }

      const is_heart = source.target === "heart";
      const target = is_heart ? hud.heart.target : hud.avatar_target;
      const copies = Math.max( 1, source.copies ?? 1 );

      for ( let n = 0; n < copies; n++ )
      {
        const finish = this.flyIcon( ctx, root, source.img_id, source.center, target, source.size, now + n * RewardFlyToHud.FLY_STAGGER_MS );
        if ( is_heart )
        {
          heart_finish = Math.max( heart_finish, finish );
          heart_size = Math.max( heart_size, source.size );
        }
        else
        {
          avatar_finish = Math.max( avatar_finish, finish );
          avatar_size = Math.max( avatar_size, source.size );
        }
      }
    }

    let max_finish = Math.max( now, coin_finish, heart_finish, avatar_finish );
    const stats_after = StatisticsStorage.load();

    if ( coin_finish > 0 )
    {
      Vfx2DManager.addCoinCounterImpact( hud.coin.target, coin_size, coin_finish, RewardFlyToHud.IMPACT_MS );
      this.schedule( ctx, root, now, coin_finish, () => AdventureRewardHud.animateCoins( hud, stats_after.coins, performance.now(), RewardFlyToHud.COUNT_UP_MS ) );
      max_finish = Math.max( max_finish, coin_finish + RewardFlyToHud.COUNT_UP_MS );
    }
    if ( heart_finish > 0 )
    {
      Vfx2DManager.addCoinCounterImpact( hud.heart.target, heart_size, heart_finish, RewardFlyToHud.IMPACT_MS );
      this.schedule( ctx, root, now, heart_finish, () => AdventureRewardHud.refreshHearts( hud, StatisticsStorage.load(), Date.now() ) );
    }
    if ( avatar_finish > 0 )
      Vfx2DManager.addCoinCounterImpact( hud.avatar_target, avatar_size * 1.3, avatar_finish, RewardFlyToHud.IMPACT_MS );

    const hide_at = max_finish + RewardFlyToHud.HUD_HOLD_MS;
    this.schedule( ctx, root, now, hide_at, () => this.hide( root, hud ) );
    return hide_at + RewardFlyToHud.HUD_HIDE_MS;
  }

  static flyIcon( ctx: CanvasRenderingContext2D, root: UIElement, img_id: ImgID, from_abs: Vector2, to_abs: Vector2, size: number, start_time: number ): number
  {
    const origin = root.getAbsolutePosition();
    const from = new Vector2( from_abs.x - origin.x, from_abs.y - origin.y );
    const to = new Vector2( to_abs.x - origin.x, to_abs.y - origin.y );

    const icon = new UIImage( ctx, from.x - size / 2, from.y - size / 2, size, size, img_id );
    icon.setClickable( false );
    root.addChild( icon );

    const side = Math.random() < 0.5 ? -1 : 1;
    const mid_x = ( from.x + to.x ) / 2 + side * ( 40 + Math.random() * 50 );
    const mid_y = Math.min( from.y, to.y ) - ( 60 + Math.random() * 50 );

    const path = ( t: number ): Vector2 =>
    {
      const inv = 1 - t;
      const x = inv * inv * from.x + 2 * inv * t * mid_x + t * t * to.x;
      const y = inv * inv * from.y + 2 * inv * t * mid_y + t * t * to.y;
      return new Vector2( x - size / 2, y - size / 2 );
    };
    const apply = ( t: number ): void => icon.setPixelPos( path( t ), true );

    const duration = RewardFlyToHud.FLY_MS;
    const fly = new TransformValue( 0, 1, start_time, duration, apply, { easing: Easing.EaseInQuad } );
    fly.setDomSample( t => ( { pos: path( t ) } ) );
    icon.addTransform( fly );
    icon.addTransform( new TransformScale(
      icon,
      true,
      new Vector2( RewardFlyToHud.FLY_START_SCALE, RewardFlyToHud.FLY_START_SCALE ),
      new Vector2( RewardFlyToHud.FLY_END_SCALE, RewardFlyToHud.FLY_END_SCALE ),
      start_time,
      duration,
      { easing: Easing.EaseInQuad }
    ) );

    const fade = new TransformOpacity( icon, true, 1, 0, start_time + duration * 0.75, duration * 0.25, { easing: Easing.EaseInQuad } );
    fade.setOnFinished( () =>
    {
      root.removeChild( icon );
      root.forceMakeShouldBeRendered();
    } );
    icon.addTransform( fade );

    return start_time + duration;
  }

  static schedule( ctx: CanvasRenderingContext2D, root: UIElement, now: number, at: number, cb: () => void ): void
  {
    const host = new UIElementHolder( ctx, 0, 0, 1, 1, false );
    host.setClickable( false );
    root.addChild( host );

    const trigger = new TransformValue( 0, 1, now, Math.max( 1, at - now ), () => {} );
    trigger.setOnFinished( () =>
    {
      root.removeChild( host );
      cb();
    } );
    host.addTransform( trigger );
  }

  static releaseVfxOverlay(): void
  {
    try
    {
      if ( !Vfx2DRuntime.hasActiveEffects() )
        Game.instance.setVfxOverlayCanvasesVisible( false );
    }
    catch ( e )
    {
      console.warn( "RewardFlyToHud.releaseVfxOverlay: hasActiveEffects() check failed, leaving VFX overlay canvases as-is", e );
    }
  }

  private static hide( root: UIElement, hud: AdventureRewardHudRefs ): void
  {
    const fade = new TransformOpacity( hud.row, true, null, 0, performance.now(), RewardFlyToHud.HUD_HIDE_MS, { easing: Easing.EaseInQuad } );
    fade.setOnFinished( () =>
    {
      root.removeChild( hud.row );
      root.forceMakeShouldBeRendered();
      this.releaseVfxOverlay();
    } );
    hud.row.addTransform( fade );
  }
}
